import React, { useState } from "react";
import { alpha, Box, IconButton, Link, Menu, MenuItem, Paper, Typography } from "@mui/material";
import { Done, DoneAll, AccessTime, KeyboardArrowDown, Reply } from "@mui/icons-material";
import { useThemeContext } from "../../../../core/contexts/ThemeContext";
import { IMensagemRespondida, StatusMensagem } from "../../../interface/atendimento/IMensagem";
import { TipoEmissorMensagem } from "../../../enum/atendimento/TipoEmissorMensagem.enum";
import { cores, raio, superficieModo, texto } from "../identidade";

interface ChatBubbleProps {
    text: string;
    timestamp: string;
    position: "left" | "right";
    color?: string;
    status?: StatusMensagem;
    editada?: boolean;
    mensagemRespondida?: IMensagemRespondida;
    onReply?: () => void;
}

const StatusIcon: React.FC<{ status?: StatusMensagem }> = ({ status }) => {
    if (!status) {
        return <AccessTime sx={{ fontSize: 12, color: 'text.disabled' }} />;
    }
    if (status === 'LIDA') {
        return <DoneAll sx={{ fontSize: 14, color: '#53bdeb' }} />;
    }
    if (status === 'RECEBIDA') {
        return <DoneAll sx={{ fontSize: 14, color: 'text.disabled' }} />;
    }
    return <Done sx={{ fontSize: 14, color: 'text.disabled' }} />;
};

const ChatBubble: React.FC<ChatBubbleProps> = ({ text, timestamp, position, color, status, editada, mensagemRespondida, onReply }) => {
    const isRight = position === "right";
    const { theme, themeName } = useThemeContext();
    const [hovered, setHovered] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    // Bolha da loja em tint da marca, do cliente em overlay neutro — o mesmo
    // par de tons da landing, o que dispensa contorno pesado e cauda.
    const bgColor = color || (isRight
        ? alpha(cores.marca[500], themeName === 'dark' ? 0.22 : 0.14)
        : superficieModo(themeName, themeName === 'dark' ? 0.07 : 0.05));

    const corBorda = isRight
        ? alpha(cores.marca[500], themeName === 'dark' ? 0.42 : 0.24)
        : superficieModo(themeName, 0.09);

    const quoteBgColor = superficieModo(themeName, themeName === 'dark' ? 0.09 : 0.06);

    const quoteBarColor = isRight ? cores.marca[500] : theme.palette.text.secondary;

    // Função para detectar e transformar URLs em links clicáveis
    const linkify = (text: string) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const parts = text.split(urlRegex);

        return parts.map((part, index) => {
            if (part.match(urlRegex)) {
                return (
                    <Link
                        key={index}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: themeName === 'dark' ? cores.marca[300] : cores.marca[600],
                            textDecoration: 'underline',
                            wordBreak: 'break-all'
                        }}
                    >
                        {part}
                    </Link>
                );
            }
            return part;
        });
    };

    return (
        <Box
            display="flex"
            justifyContent={isRight ? "flex-end" : "flex-start"}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Paper
                elevation={0}
                sx={{
                    px: 1.6,
                    py: 1.15,
                    maxWidth: "82%",
                    backgroundColor: bgColor,
                    border: `1px solid ${corBorda}`,
                    backgroundImage: 'none',
                    position: "relative",
                    borderRadius: raio.bolha,
                }}
            >
                {/* Chevron dropdown — aparece no canto superior direito ao hover */}
                {onReply && (
                    <>
                        <IconButton
                            size="small"
                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                            sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                opacity: hovered || Boolean(menuAnchor) ? 1 : 0,
                                transition: 'opacity 0.15s',
                                p: 0.25,
                                color: 'text.secondary',
                                zIndex: 1,
                            }}
                        >
                            <KeyboardArrowDown sx={{ fontSize: 16 }} />
                        </IconButton>
                        <Menu
                            anchorEl={menuAnchor}
                            open={Boolean(menuAnchor)}
                            onClose={() => setMenuAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <MenuItem
                                dense
                                onClick={() => {
                                    setMenuAnchor(null);
                                    onReply();
                                }}
                                sx={{ gap: 1 }}
                            >
                                <Reply sx={{ fontSize: 16 }} />
                                Responder
                            </MenuItem>
                        </Menu>
                    </>
                )}

                {/* Preview da mensagem respondida */}
                {mensagemRespondida && (
                    <Box
                        sx={{
                            borderLeft: `3px solid ${quoteBarColor}`,
                            borderRadius: 1,
                            backgroundColor: quoteBgColor,
                            px: 1,
                            py: 0.5,
                            mb: 1,
                            maxWidth: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: quoteBarColor,
                                display: 'block',
                                mb: 0.25,
                            }}
                        >
                            {mensagemRespondida.emissor === TipoEmissorMensagem.CLIENTE ? 'Cliente' : 'Você'}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                wordBreak: 'break-word',
                            }}
                        >
                            {mensagemRespondida.texto || 'Mensagem'}
                        </Typography>
                    </Box>
                )}

                <Typography
                    sx={{
                        fontSize: '12.5px',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    }}
                >
                    {linkify(text)}
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.5,
                        mt: 0.5,
                    }}
                >
                    {editada && (
                        <Typography sx={{ ...texto.apoio, opacity: 0.55, fontStyle: 'italic' }}>
                            Editada
                        </Typography>
                    )}
                    <Typography sx={{ ...texto.apoio, opacity: 0.6 }}>
                        {timestamp}
                    </Typography>
                    {isRight && <StatusIcon status={status} />}
                </Box>
            </Paper>
        </Box>
    );
};

export default ChatBubble;
